import React, { createContext, useContext, useState, ReactNode } from 'react';
import {
  approveInvoice as apiApproveInvoice,
  rejectInvoice as apiRejectInvoice,
  approveBillingItem as apiApproveBillingItem,
  approveBillingItemsBulk as apiApproveBillingItemsBulk,
  rejectBillingItem as apiRejectBillingItem,
  reviewBillingItem as apiReviewBillingItem,
  reviewBillingItemsBulk as apiReviewBillingItemsBulk,
  fetchBillingItemsByOrder
} from '../api/billingInvoicingApi';

// Types for context state
export type BillingCompany = {
  id: string;
  name: string;
};

export type BillingOrder = {
  id: string;
  status: string;
  orderNumber?: string;
  date?: string; // ISO date string
  amount?: number;
  requestReferenceId?: string;
  modificationDate?: string;
  creationDate?: string;
  // Add more fields as needed for display
};

export type BillingItem = {
  id: string;
  orderId: string;
  serviceTypeId?: number;
  description?: string;
  unit?: string;
  quantity?: number;
  unitPrice?: number;
  netAmount?: number;
  vatRate?: number;
  vatAmount?: number;
  grossAmount?: number;
  servicePeriodFrom?: string;
  servicePeriodTo?: string;
  creationDate?: string;
  createdBy?: string;
  modificationDate?: string;
  modifiedBy?: string;
  verifiedBy?: string;
  verifiedAt?: string;
  approvedBy?: string;
  approvedAt?: string;
  comment?: string;
  status?: string;
  createdByName?: string;
  approvedByName?: string;
  order?: {
    request?: {
      referenceId?: string;
    };
  };
};

export type InvoiceAuditLogEntry = {
  action: string;
  userId: string;
  userName: string;
  timestamp: string;
  comment?: string;
};

export type Invoice = {
  id: string;
  invoiceId?: number;
  prtId?: number;
  rtuId?: string;
  invoiceNumber?: string;
  invoiceDate?: string;
  dueDate?: string;
  totalAmount?: number;
  vatAmount?: number;
  grossTotal?: number;
  status?: string;
  creationDate?: string;
  billingItems?: BillingItem[];
  createdBy?: string;
  createdByName?: string;
  auditLog?: InvoiceAuditLogEntry[];
};

export type BillingStep =
  | 'company-selection'
  | 'order-selection'
  | 'billing-item-summary'
  | 'invoice-creation'
  | 'invoice-details';

export interface BillingInvoicingContextType {
  selectedCompany: BillingCompany | null;
  setSelectedCompany: (company: BillingCompany | null) => void;
  selectedOrders: BillingOrder[];
  setSelectedOrders: (orders: BillingOrder[]) => void;
  billingItems: BillingItem[];
  setBillingItems: (items: BillingItem[]) => void;
  invoice: Invoice | null;
  setInvoice: (invoice: Invoice | null) => void;
  currentStep: BillingStep;
  setCurrentStep: (step: BillingStep) => void;
  approveInvoice: (
    prtId: string,
    invoiceId: string,
    approverId: string,
    comment?: string
  ) => Promise<void>;
  rejectInvoice: (
    prtId: string,
    invoiceId: string,
    approverId: string,
    comment?: string
  ) => Promise<void>;
  approveLoading: boolean;
  rejectLoading: boolean;
  approveError: string | null;
  rejectError: string | null;
  approveBillingItem: (
    billingItemId: string,
    approverId: string,
    comment?: string
  ) => Promise<void>;
  approveBillingItemsBulk: (
    billingItemIds: string[],
    approverId: string,
    comment?: string
  ) => Promise<void>;
  rejectBillingItem: (
    billingItemId: string,
    userId: string,
    comment?: string
  ) => Promise<void>;
  billingItemActionLoading: boolean;
  billingItemActionError: string | null;
  reviewBillingItem: (
    billingItemId: string,
    reviewerId: string,
    comment?: string
  ) => Promise<void>;
  reviewBillingItemsBulk: (
    billingItemIds: string[],
    reviewerId: string,
    comment?: string
  ) => Promise<void>;
  reviewBillingItemLoading: boolean;
  reviewBillingItemError: string | null;
  fetchAndSetBillingItemsForOrders: (orderIds: string[]) => Promise<void>;
}

const BillingInvoicingContext = createContext<
  BillingInvoicingContextType | undefined
>(undefined);

export const BillingInvoicingProvider = ({
  children
}: {
  children: ReactNode;
}) => {
  const [selectedCompany, setSelectedCompany] = useState<BillingCompany | null>(
    null
  );
  const [selectedOrders, setSelectedOrders] = useState<BillingOrder[]>([]);
  const [billingItems, setBillingItems] = useState<BillingItem[]>([]);
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [currentStep, setCurrentStep] =
    useState<BillingStep>('company-selection');
  const [approveLoading, setApproveLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [approveError, setApproveError] = useState<string | null>(null);
  const [rejectError, setRejectError] = useState<string | null>(null);
  const [billingItemActionLoading, setBillingItemActionLoading] =
    useState(false);
  const [billingItemActionError, setBillingItemActionError] = useState<
    string | null
  >(null);
  const [reviewBillingItemLoading, setReviewBillingItemLoading] =
    useState(false);
  const [reviewBillingItemError, setReviewBillingItemError] = useState<
    string | null
  >(null);

  const approveInvoice = async (
    prtId: string,
    invoiceId: string,
    approverId: string,
    comment?: string
  ) => {
    setApproveLoading(true);
    setApproveError(null);
    try {
      const updated = await apiApproveInvoice(
        prtId,
        invoiceId,
        approverId,
        comment
      );
      setInvoice(updated);
    } catch (err: unknown) {
      if (
        err &&
        typeof err === 'object' &&
        'message' in err &&
        typeof (err as { message?: unknown }).message === 'string'
      ) {
        setApproveError((err as { message: string }).message);
      } else {
        setApproveError('Failed to approve invoice.');
      }
    } finally {
      setApproveLoading(false);
    }
  };

  const rejectInvoice = async (
    prtId: string,
    invoiceId: string,
    approverId: string,
    comment?: string
  ) => {
    setRejectLoading(true);
    setRejectError(null);
    try {
      const updated = await apiRejectInvoice(
        prtId,
        invoiceId,
        approverId,
        comment
      );
      setInvoice(updated);
    } catch (err: unknown) {
      if (
        err &&
        typeof err === 'object' &&
        'message' in err &&
        typeof (err as { message?: unknown }).message === 'string'
      ) {
        setRejectError((err as { message: string }).message);
      } else {
        setRejectError('Failed to reject invoice.');
      }
    } finally {
      setRejectLoading(false);
    }
  };

  const approveBillingItem = async (
    billingItemId: string,
    approverId: string,
    comment?: string
  ) => {
    setBillingItemActionLoading(true);
    setBillingItemActionError(null);
    try {
      const updated = await apiApproveBillingItem(
        billingItemId,
        approverId,
        comment
      );
      setBillingItems((prev) =>
        prev.map((item) => (item.id === updated.id ? updated : item))
      );
    } catch (err: any) {
      setBillingItemActionError(
        err?.message || 'Failed to approve billing item.'
      );
    } finally {
      setBillingItemActionLoading(false);
    }
  };

  const approveBillingItemsBulk = async (
    billingItemIds: string[],
    approverId: string,
    comment?: string
  ) => {
    setBillingItemActionLoading(true);
    setBillingItemActionError(null);
    try {
      const updatedItems = await apiApproveBillingItemsBulk(
        billingItemIds,
        approverId,
        comment
      );
      setBillingItems((prev) => {
        const updatedMap = new Map(updatedItems.map((item) => [item.id, item]));
        return prev.map((item) => updatedMap.get(item.id) || item);
      });
    } catch (err: any) {
      setBillingItemActionError(
        err?.message || 'Failed to approve billing items.'
      );
    } finally {
      setBillingItemActionLoading(false);
    }
  };

  const rejectBillingItem = async (
    billingItemId: string,
    userId: string,
    comment?: string
  ) => {
    setBillingItemActionLoading(true);
    setBillingItemActionError(null);
    try {
      const updated = await apiRejectBillingItem(
        billingItemId,
        userId,
        comment
      );
      setBillingItems((prev) =>
        prev.map((item) => (item.id === updated.id ? updated : item))
      );
    } catch (err: any) {
      setBillingItemActionError(
        err?.message || 'Failed to reject billing item.'
      );
    } finally {
      setBillingItemActionLoading(false);
    }
  };

  const reviewBillingItem = async (
    billingItemId: string,
    reviewerId: string,
    comment?: string
  ) => {
    setReviewBillingItemLoading(true);
    setReviewBillingItemError(null);
    try {
      const updated = await apiReviewBillingItem(
        billingItemId,
        reviewerId,
        comment
      );
      setBillingItems((prev) =>
        prev.map((item) => (item.id === updated.id ? updated : item))
      );
    } catch (err: any) {
      setReviewBillingItemError(
        err?.message || 'Failed to review billing item.'
      );
    } finally {
      setReviewBillingItemLoading(false);
    }
  };

  const reviewBillingItemsBulk = async (
    billingItemIds: string[],
    reviewerId: string,
    comment?: string
  ) => {
    setReviewBillingItemLoading(true);
    setReviewBillingItemError(null);
    try {
      const updatedItems = await apiReviewBillingItemsBulk(
        billingItemIds,
        reviewerId,
        comment
      );
      setBillingItems((prev) => {
        const updatedMap = new Map(updatedItems.map((item) => [item.id, item]));
        return prev.map((item) => updatedMap.get(item.id) || item);
      });
    } catch (err: any) {
      setReviewBillingItemError(
        err?.message || 'Failed to review billing items.'
      );
    } finally {
      setReviewBillingItemLoading(false);
    }
  };

  const fetchAndSetBillingItemsForOrders = async (orderIds: string[]) => {
    const allItems: BillingItem[] = [];
    for (const orderId of orderIds) {
      try {
        const items = await fetchBillingItemsByOrder(orderId);
        allItems.push(...items);
      } catch (e) {
        // Optionally handle error per order
      }
    }
    setBillingItems(allItems);
  };

  return (
    <BillingInvoicingContext.Provider
      value={{
        selectedCompany,
        setSelectedCompany,
        selectedOrders,
        setSelectedOrders,
        billingItems,
        setBillingItems,
        invoice,
        setInvoice,
        currentStep,
        setCurrentStep,
        approveInvoice,
        rejectInvoice,
        approveLoading,
        rejectLoading,
        approveError,
        rejectError,
        approveBillingItem,
        approveBillingItemsBulk,
        rejectBillingItem,
        billingItemActionLoading,
        billingItemActionError,
        reviewBillingItem,
        reviewBillingItemsBulk,
        reviewBillingItemLoading,
        reviewBillingItemError,
        fetchAndSetBillingItemsForOrders
      }}
    >
      {children}
    </BillingInvoicingContext.Provider>
  );
};

export const useBillingInvoicing = () => {
  const context = useContext(BillingInvoicingContext);
  if (!context) {
    throw new Error(
      'useBillingInvoicing must be used within a BillingInvoicingProvider'
    );
  }
  return context;
};
